/* eslint-disable react-refresh/only-export-components --
 * Provider + useTelemetry hook live in one file by design. See
 * src/framework/FrameworkContext.tsx for the rationale.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { ApplicationInsights } from '@microsoft/applicationinsights-web';

import {
  trackEvent as track,
  trackException as trackEx,
  setTelemetryUser,
  clearTelemetryUser,
} from './appInsights';

/**
 * React Context binding for Application Insights.
 *
 * Components use the `useTelemetry()` hook to fire custom events and
 * exceptions without touching the SDK directly. When AI is not
 * configured (local dev without VITE_APPINSIGHTS_CONNECTION_STRING),
 * the hook returns no-op implementations so call sites don't need to
 * guard.
 *
 * The provider should wrap the routed app once in main.tsx, with the
 * ApplicationInsights instance from initAppInsights() (or null if AI
 * is disabled in this environment).
 */

type TelemetryApi = {
  trackEvent: (
    name: string,
    properties?: Record<string, string | number | boolean>,
  ) => void;
  trackException: (
    error: Error,
    properties?: Record<string, string | number | boolean>,
  ) => void;
  setUser: (oid: string) => void;
  clearUser: () => void;
};

const noopApi: TelemetryApi = {
  trackEvent: () => undefined,
  trackException: () => undefined,
  setUser: () => undefined,
  clearUser: () => undefined,
};

const AppInsightsContext = createContext<TelemetryApi>(noopApi);

type Props = {
  instance: ApplicationInsights | null;
  children: ReactNode;
};

export function AppInsightsProvider({ instance, children }: Props): JSX.Element {
  const api = useMemo<TelemetryApi>(
    () => ({
      trackEvent: (name, properties) => track(instance, name, properties),
      trackException: (error, properties) => trackEx(instance, error, properties),
      setUser: (oid) => setTelemetryUser(instance, oid),
      clearUser: () => clearTelemetryUser(instance),
    }),
    [instance],
  );

  return (
    <AppInsightsContext.Provider value={api}>
      {children}
    </AppInsightsContext.Provider>
  );
}

export function useTelemetry(): TelemetryApi {
  return useContext(AppInsightsContext);
}
